import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';
let inflightRefresh = null;
function hasVisibleWidgetData() {
  const f = app.forum;
  if (!f) return false;
  if (f.attribute('statserCanViewOnline')) return true;
  if (f.attribute('statserDiscussionsCount') != null) return true;
  if (f.attribute('statserPostsCount') != null) return true;
  if (f.attribute('statserUsersCount') != null) return true;
  try {
    var _statserLatestUser, _ref;
    if ((_statserLatestUser = (_ref = f).statserLatestUser) != null && _statserLatestUser.call(_ref)) return true;
  } catch (e) {}
  return false;
}
export function refreshForumData() {
  if (!hasVisibleWidgetData()) return Promise.resolve();
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = app.store.find('forums', {
    include: 'statserOnlineUsers,statserLatestUser',
    'fields[users]': 'username,displayName,avatarUrl,slug'
  }).catch(() => {}).then(() => {
    inflightRefresh = null;
    m.redraw();
  });
  return inflightRefresh;
}

/**
 * True if the current discussion belongs to FoF Byōbu (private conversation).
 * `isPrivateDiscussion` is added to the Discussion model by that extension;
 * it is simply absent when Byōbu isn't installed, so this stays a no-op then.
 */
function isPrivateDiscussion(discussion) {
  try {
    var _discussion$isPrivate;
    return !!(discussion != null && (_discussion$isPrivate = discussion.isPrivateDiscussion) != null && _discussion$isPrivate.call(discussion));
  } catch (e) {
    return false;
  }
}

/** Route names Byōbu registers on the frontend for private-conversation pages. */
const BYOBU_ROUTES = new Set(['byobuPrivate', 'byobuUserPrivate', 'byobuComposer']);
export function describeCurrentRoute() {
  try {
    var _window$location;
    const current = app.current;
    if (!current || typeof current.get !== 'function') return null;
    const routeName = current.get('routeName');
    if (!routeName) return null;
    const pre = 'forumaker-statser.forum.routes.';

    // FoF Arena compatibility: battle URLs aren't exposed as a distinct Mithril
    // route, so we match on the URL itself per Arena's "bitva" path segment.
    if (typeof window !== 'undefined' && (_window$location = window.location) != null && (_window$location = _window$location.pathname) != null && _window$location.includes('bitva')) {
      return {
        route: routeName,
        label: extractText(app.translator.trans(pre + 'arena')),
        standalone: true
      };
    }
    if (BYOBU_ROUTES.has(routeName)) {
      return {
        route: routeName,
        label: extractText(app.translator.trans(pre + 'private_discussion'))
      };
    }
    const base = routeName.split('.')[0];
    switch (base) {
      case 'index':
        return {
          route: routeName,
          label: extractText(app.translator.trans(pre + 'index'))
        };
      case 'discussion':
        {
          var _discussion$id, _ref2, _ref3, _discussion$title, _discussion$title2, _discussion$attribute, _data;
          const discussion = current.get('discussion');
          const discussionId = (discussion == null || (_discussion$id = discussion.id) == null ? void 0 : _discussion$id.call(discussion)) != null ? String(discussion.id()) : undefined;
          if (isPrivateDiscussion(discussion)) {
            return {
              route: routeName,
              label: extractText(app.translator.trans(pre + 'private_discussion')),
              discussionId
            };
          }

          // No `document.title` fallback here on purpose: that string can't be
          // verified for privacy, and DiscussionPage sets the tab title before
          // `app.current.set('discussion', ...)` runs — so during that window
          // it could reveal a private conversation's real title even though
          // `discussion` itself isn't populated yet to check. When we don't
          // have the model, we simply don't report a title; the server also
          // independently re-checks privacy by ID as a second line of defense.
          const rawTitle = (_ref2 = (_ref3 = (_discussion$title = discussion == null || (_discussion$title2 = discussion.title) == null ? void 0 : _discussion$title2.call(discussion)) != null ? _discussion$title : discussion == null || (_discussion$attribute = discussion.attribute) == null ? void 0 : _discussion$attribute.call(discussion, 'title')) != null ? _ref3 : discussion == null || (_data = discussion.data) == null || (_data = _data.attributes) == null ? void 0 : _data.title) != null ? _ref2 : null;
          const title = rawTitle ? rawTitle.replace(/\s+[-–]\s+\S+$/, '').trim() || rawTitle : null;
          return {
            route: routeName,
            label: title ? extractText(app.translator.trans(pre + 'discussion', {
              title: truncate(title)
            })) : extractText(app.translator.trans(pre + 'generic')),
            discussionId
          };
        }
      case 'tag':
        {
          var _tag$name;
          // flarum/tags never calls `app.current.set('tag', ...)` — it only
          // exposes the current tag via an instance method on the IndexPage
          // component (`currentTag()`), which isn't reachable from here. The
          // slug is available from the route itself (`/t/:tags`), so we look
          // the model up in the store the same way flarum/tags does internally.
          const slug = m.route.param('tags');
          const tag = slug ? app.store.getBy('tags', 'slug', slug) : undefined;
          const name = tag == null || (_tag$name = tag.name) == null ? void 0 : _tag$name.call(tag);
          return {
            route: routeName,
            label: name ? extractText(app.translator.trans(pre + 'tag', {
              name: truncate(name)
            })) : extractText(app.translator.trans(pre + 'tags'))
          };
        }
      case 'tags':
        return {
          route: routeName,
          label: extractText(app.translator.trans(pre + 'tags'))
        };
      case 'user':
        {
          var _user$displayName;
          const user = current.get('user');
          const name = user == null || (_user$displayName = user.displayName) == null ? void 0 : _user$displayName.call(user);
          return {
            route: routeName,
            label: name ? extractText(app.translator.trans(pre + 'user', {
              name: truncate(name)
            })) : extractText(app.translator.trans(pre + 'generic'))
          };
        }
      case 'settings':
        return {
          route: routeName,
          label: extractText(app.translator.trans(pre + 'settings'))
        };
      case 'notifications':
        return {
          route: routeName,
          label: extractText(app.translator.trans(pre + 'notifications'))
        };
      case 'flags':
        return {
          route: routeName,
          label: extractText(app.translator.trans(pre + 'flags'))
        };
      case 'search':
        return {
          route: routeName,
          label: extractText(app.translator.trans(pre + 'search'))
        };
      case 'page':
        {
          var _page$title, _page$title2, _page$attribute;
          const page = current.get('page');
          const pageTitle = (_page$title = page == null || (_page$title2 = page.title) == null ? void 0 : _page$title2.call(page)) != null ? _page$title : page == null || (_page$attribute = page.attribute) == null ? void 0 : _page$attribute.call(page, 'title');
          return {
            route: routeName,
            label: pageTitle ? extractText(app.translator.trans(pre + 'discussion', {
              title: truncate(pageTitle)
            })) : extractText(app.translator.trans(pre + 'generic'))
          };
        }
      default:
        return {
          route: routeName,
          label: extractText(app.translator.trans(pre + 'generic'))
        };
    }
  } catch (e) {
    return null;
  }
}
function truncate(text, max) {
  if (max === void 0) {
    max = 60;
  }
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
}
let heartbeatTimer = null;
let heartbeatStopped = false;
function intervalMs() {
  var _app$forum;
  const seconds = Number((_app$forum = app.forum) == null ? void 0 : _app$forum.attribute('statserHeartbeatInterval')) || 45;
  return Math.max(15, seconds) * 1000;
}
function fireHeartbeat() {
  try {
    var _app$current, _app$current$get;
    if (heartbeatStopped) return;
    if (document.visibilityState !== 'visible') return;
    if (!app.forum || !app.forum.attribute('statserEnableHeartbeat')) return;
    const isAuthenticated = !!(app.session && app.session.user);
    const onIndex = ((_app$current = app.current) == null || (_app$current$get = _app$current.get) == null ? void 0 : _app$current$get.call(_app$current, 'routeName')) === 'index';
    const canReport = !!app.forum.attribute('statserCanReportCurrentPage');
    const body = {};
    if (canReport) {
      const described = describeCurrentRoute();
      if (described) {
        body.route = described.route;
        body.label = described.label;
        if (described.standalone) body.standalone = '1';
        if (described.discussionId) body.discussionId = described.discussionId;
      }
    }
    if (!isAuthenticated && !app.forum.attribute('statserShowOnlineGuests')) {
      if (Object.keys(body).length === 0) return;
    }
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/statser/heartbeat',
      body,
      background: true,
      errorHandler: () => {}
    }).catch(err => {
      if (err && err.status === 401 && isAuthenticated) {
        heartbeatStopped = true;
        if (heartbeatTimer) {
          clearInterval(heartbeatTimer);
          heartbeatTimer = null;
        }
      }
    });
    if (onIndex && hasVisibleWidgetData()) {
      refreshForumData();
    }
  } catch (e) {}
}
let started = false;
export function startPresence() {
  if (started) return;
  started = true;
  heartbeatTimer = setInterval(fireHeartbeat, intervalMs());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') fireHeartbeat();
  });
  if (!app.session || !app.session.user) {
    setTimeout(fireHeartbeat, 0);
  }

  // No Pusher/WebSocket dependency: refresh triggers are SPA return-to-index
  // (see forum/index.tsx), tab refocus (above), and the heartbeat tick itself.
  // That's the same "live enough" combination flarum-ext-forum-stats-widget
  // uses, and it doesn't depend on flarum/pusher being installed and
  // configured, which almost no forum has.
}
flarum.reg.add('forumaker-statser', 'forum/utils/presence', { refreshForumData: refreshForumData,describeCurrentRoute: describeCurrentRoute,startPresence: startPresence, });